import React, { useLayoutEffect, useRef } from 'react';
import ChatMessage from './ChatMessage';
import ChatErrorBoundary, { messageRowFallback } from './ChatErrorBoundary';
import {
  keepSeriesBookCardOfferInView,
  pendingBookCardOfferStampKey,
} from './SeriesBookCardOffer';
import './Chat.css';

const MessageList = ({
  messages,
  welcomeMessage,
  onBookmark,
  citationMap,
  highlightedMessageId,
  bookmarkedMessages = {},
  // History pagination (issue #202)
  hasMoreMessages = false,
  isLoadingOlder = false,
  onLoadOlder,
  // Session suggestion props
  sessionId,
  storyId,
  projectId,
  canSaveToSeriesNotes = false,
  onNavigateToSession,
  pendingFoundation,
  lastAssistantMsgId,
  onSuggestionDismissed,
  onSeriesFocusOfferAccept,
  onSeriesBookCardAccept,
  onPlotReadyOfferAccept,
  onPlotReadyOfferDecline,
  onBeatWorkshopAccept,
  onRegenerate,
  chatModelId = null,
  messageCount = null,
  discovery = false,
  estimateInputTokens = null,
  onNudgeVote,
  muteHint = null,
  emptyInvitation = null,
  replyFollow = null,
  ideaOffersByMessage = null,
  onIdeaOfferResolved,
}) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesInnerRef = useRef(null);
  // Distinguish "older messages prepended on scroll-up" from "new message
  // appended": the former must preserve scroll position (not jump to bottom).
  const prependRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  // Whether the user is parked at/near the bottom. Drives auto-pin so we never
  // yank someone who has scrolled up to read history. Default true: a fresh
  // load lands at the bottom (issue #205).
  const isNearBottomRef = useRef(true);
  // #741 / #744 / #748: a new book-card stamp is a mid-thread card.
  // Pinning the composer after stream settle leaves it above the fold.
  // Hold the compact offer (title + one line + Accept) in view. If it
  // cannot fit, pin the title, not Accept. Keep that pin across stamp
  // id-settlement so scroll-to-bottom cannot land on Accept alone.
  const prevOfferStampRef = useRef('');
  const keepOfferStampRef = useRef('');
  const hadMessagesRef = useRef(false);
  const prevMessagesSigRef = useRef('');
  // Last message id seen by the layout effect: a new last message from the
  // writer means they just sent, so the view follows the reply (#2527).
  const prevLastIdRef = useRef(null);

  // Snap instantly to the live bottom (issue #205). The previous smooth
  // scrollIntoView aimed at the bottom as it existed when `messages` changed,
  // but more height arrives after that (reply layout, citations, and the
  // suggestion cards that render below the last assistant message), leaving the
  // view parked partway up. An instant scroll against the current scrollHeight
  // plus the ResizeObserver below keeps us pinned through late-arriving height.
  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container && !highlightedMessageId) {
      container.scrollTop = container.scrollHeight;
    }
  };

  // Preserve viewport on prepend; otherwise snap to bottom on new messages —
  // but only when the user was already at the bottom, so a message arriving
  // while they read history doesn't yank them down (their own send keeps them
  // near the bottom, so it still snaps then). useLayoutEffect so the position
  // is corrected before the browser paints. A newly stamped book-card offer
  // wins over the composer pin (#741 / #744 / #752 / #754): keep the
  // small title card (title + Accept) in view. If it cannot fit, pin
  // the title, not the footer, and not the whole transcript.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    const stamp = pendingBookCardOfferStampKey(messages, sessionId);
    const messagesSig = (messages || []).map((msg) => msg.id).join(',');
    const isNewStamp = Boolean(stamp) && stamp !== prevOfferStampRef.current && hadMessagesRef.current;
    if (isNewStamp) keepOfferStampRef.current = stamp;
    else if (!stamp) keepOfferStampRef.current = '';
    else if (messagesSig !== prevMessagesSigRef.current) {
      const prevCount = prevMessagesSigRef.current
        ? prevMessagesSigRef.current.split(',').filter(Boolean).length
        : 0;
      const nextCount = messages?.length || 0;
      // Same pending stamp, ids may have settled — keep pinning.
      // A new message in the thread releases the pin.
      if (nextCount !== prevCount) keepOfferStampRef.current = '';
    }
    prevOfferStampRef.current = stamp;
    prevMessagesSigRef.current = messagesSig;
    hadMessagesRef.current = !!(messages && messages.length);

    // The writer just sent: follow the reply even if they were scrolled up.
    // A prepend changes the head of the list, never the last message.
    const lastMessage = messages && messages.length ? messages[messages.length - 1] : null;
    const lastId = lastMessage ? lastMessage.id : null;
    if (lastId !== prevLastIdRef.current && lastMessage && lastMessage.speaker === 'user') {
      isNearBottomRef.current = true;
    }
    prevLastIdRef.current = lastId;

    if (prependRef.current && container) {
      // Keep the previously-visible message under the viewport: shift by the
      // height gained from the newly prepended batch.
      const delta = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop = prevScrollTopRef.current + delta;
      prependRef.current = false;
    } else if (keepOfferStampRef.current) {
      keepSeriesBookCardOfferInView(container);
    } else if (isNearBottomRef.current) {
      scrollToBottom();
      isNearBottomRef.current = true;
    }
  }, [messages, highlightedMessageId, sessionId]);

  // Follow late-arriving height (issue #205): the assistant reply's markdown/
  // citation layout and the pendingFoundation offramp card grow the thread AFTER the
  // `messages` array changes. Re-pin to the bottom whenever the inner content
  // grows and the user was already at the bottom (and we're not mid-prepend or
  // deep-linking to a highlighted message).
  // Deps include hasMessages (not just highlightedMessageId, as the original
  // sketch had it): MessageList mounts with an empty `messages` array — the
  // inner `.message-list__messages` node only exists once messages populate —
  // so the observer must (re)attach on that empty→populated transition (and
  // re-attach after a session switch resets messages to []).
  const hasMessages = !!(messages && messages.length > 0);
  useLayoutEffect(() => {
    const inner = messagesInnerRef.current;
    const container = messagesContainerRef.current;
    if (!inner || !container) return;
    const ro = new ResizeObserver(() => {
      if (keepOfferStampRef.current && !prependRef.current && !highlightedMessageId) {
        keepSeriesBookCardOfferInView(container);
        return;
      }
      if (isNearBottomRef.current && !prependRef.current && !highlightedMessageId) {
        container.scrollTop = container.scrollHeight;
      }
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [highlightedMessageId, hasMessages]);

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Track distance from the bottom edge so auto-pin only fires when the user
    // is already near the bottom.
    const distance = container.scrollHeight - container.scrollTop - container.clientHeight;
    isNearBottomRef.current = distance <= 120;

    // Near the top → request the next-older batch, recording the current
    // geometry so the layout effect can restore the reading position.
    if (hasMoreMessages && !isLoadingOlder && onLoadOlder && container.scrollTop <= 80) {
      prevScrollHeightRef.current = container.scrollHeight;
      prevScrollTopRef.current = container.scrollTop;
      prependRef.current = true;
      onLoadOlder();
    }
  };

  // Show welcome message for empty sessions
  if (!messages || messages.length === 0) {
    if (welcomeMessage) {
      return (
        <div className="message-list" ref={messagesContainerRef}>
          <div className="message-list__messages">
            <ChatMessage
              message={{
                id: 'welcome',
                speaker: 'assistant',
                content: welcomeMessage,
                created_at: new Date().toISOString()
              }}
              citationMap={{}}
              isHighlighted={false}
              isBookmarked={false}
            />
            {muteHint && (
              <p className="message-list__mute-hint">{muteHint}</p>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      );
    }
    if (emptyInvitation) {
      return (
        <div className="message-list" ref={messagesContainerRef}>
          <div className="message-list__messages">
            <p className="message-list__empty-invitation">{emptyInvitation}</p>
            {muteHint && (
              <p className="message-list__mute-hint">{muteHint}</p>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      );
    }
    return (
      <div className="message-list message-list--empty">
        <p className="message-list__empty-text">No messages yet. Start the conversation!</p>
        {muteHint && (
          <p className="message-list__mute-hint">{muteHint}</p>
        )}
      </div>
    );
  }

  // Regenerate is scoped to the LAST message overall (not just the last
  // assistant message) — computed live from `messages` rather than tracked
  // separately, so sending a new message (even the optimistic temp- row)
  // hides the control on the prior assistant message automatically, with no
  // separate state to keep in sync.
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1].id : null;

  return (
    <div className="message-list" ref={messagesContainerRef} onScroll={handleScroll}>
      {isLoadingOlder && (
        <div className="message-list__loading-older">Loading earlier messages…</div>
      )}
      <div className="message-list__messages" ref={messagesInnerRef}>
        {messages.map((message) => (
          <ChatErrorBoundary key={message.id} fallback={messageRowFallback(message)}>
          <ChatMessage
            message={message}
            onBookmark={onBookmark}
            citationMap={citationMap}
            isHighlighted={message.id === highlightedMessageId}
            isBookmarked={!!bookmarkedMessages[message.id]}
            sessionId={sessionId}
            storyId={storyId}
            projectId={projectId}
            canSaveToSeriesNotes={canSaveToSeriesNotes}
            onNavigateToSession={onNavigateToSession}
            pendingFoundation={message.id === lastAssistantMsgId ? pendingFoundation : null}
            onSuggestionDismissed={onSuggestionDismissed}
            onSeriesFocusOfferAccept={onSeriesFocusOfferAccept}
            onSeriesBookCardAccept={onSeriesBookCardAccept}
            onPlotReadyOfferAccept={onPlotReadyOfferAccept}
            onPlotReadyOfferDecline={onPlotReadyOfferDecline}
            onBeatWorkshopAccept={onBeatWorkshopAccept}
            isLastMessage={message.id === lastMessageId}
            onRegenerate={onRegenerate}
            chatModelId={chatModelId}
            messageCount={messageCount}
            discovery={discovery}
            estimateInputTokens={estimateInputTokens}
            onNudgeVote={onNudgeVote}
            ideaOffers={ideaOffersByMessage?.get(String(message.id)) || []}
            onIdeaOfferResolved={onIdeaOfferResolved}
          />
          </ChatErrorBoundary>
        ))}
        {replyFollow}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessageList;
